import { styled } from '@mui/material/styles';
import { Box, Card, Button, TextField, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const AuthContainer = styled(Box)(({ theme }) => ({
  fontFamily: 'Arial, sans-serif',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: '1100px',
  minHeight: '95vh',
  background: `linear-gradient(135deg, #1A1A1A 0%, ${theme.palette.secondary.main} 100%)`,
  color: theme.palette.text.primary, // Use theme color
  padding: theme.spacing(3), // Use theme spacing
}));

export const AuthCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
  width: '100%',
  maxWidth: '500px',
  padding: theme.spacing(5), // Use theme spacing
  transition: 'all 0.3s ease',
  animation: `${fadeIn} 0.6s ease-out`,
}));

export const AuthLogo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '30px',
});

export const LogoIcon = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  width: '70px',
  height: '70px',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '36px',
  fontWeight: 'bold',
  marginBottom: '15px',
  boxShadow: '0 4px 12px rgba(118, 75, 162, 0.3)', // Consider using theme shadow
}));

export const AuthStepIndicator = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '30px 0',
});

export const AuthStep = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.secondary.main : '#e6e6e6',
  color: active ? theme.palette.common.white : '#9e9e9e',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 'bold',
  fontSize: '18px',
  position: 'relative',
  transition: 'all 0.3s ease',
  boxShadow: active ? '0 3px 8px rgba(118, 75, 162, 0.3)' : 'none',
  transform: active ? 'scale(1.1)' : 'scale(1)',
}));


export const AuthStepLine = styled(Box)({
  flexGrow: 1,
  height: '3px',
  backgroundColor: '#e6e6e6',
  margin: '0 15px',
  position: 'relative',
  maxWidth: '80px',
});

export const AuthForm = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

export const IdInput = styled(TextField)({
  '& .MuiInputBase-input': {
    fontSize: '18px',
    letterSpacing: '1px',
    textAlign: 'center',
  },
});

export const VerificationCodeDisplay = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textAlign: 'center',
  marginBottom: theme.spacing(1.5),
  borderLeft: `4px solid ${theme.palette.secondary.main}`,
}));

export const CodeDisplay = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '24px',
  fontWeight: 'bold',
  color: theme.palette.secondary.dark,
  letterSpacing: '2px',
  marginTop: '5px',
}));

export const VerificationInputContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
});

export const VerificationInput = styled(TextField)({
    width: '160px',
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontSize: '24px',
    letterSpacing: '4px',
    padding: '10px 0',
  },
   '& .MuiOutlinedInput-root': { // Targeting the outlined variant if used
    borderRadius: '8px',
  },
});


export const AuthButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'secondary',
})(({ theme, secondary }) => ({
  backgroundColor: secondary ? theme.palette.grey[100] : theme.palette.secondary.main,
  color: secondary ? theme.palette.text.secondary : theme.palette.common.white,
  border: secondary ? `1px solid ${theme.palette.divider}` : 'none',
  borderRadius: '8px',
  padding: '15px',
  fontSize: '16px',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  marginTop: '10px',
  '&:hover': {
    backgroundColor: secondary ? theme.palette.grey[200] : theme.palette.secondary.light,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(118, 75, 162, 0.3)', // Consider theme shadow
  },
}));


export const AuthError = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  textAlign: 'center',
  fontSize: '14px',
  marginTop: '5px',
}));

export const AuthInfo = styled(Typography)(({ theme }) => ({
  marginTop: '20px',
  textAlign: 'center',
  color: theme.palette.text.disabled,
  fontSize: '12px',
}));

export const LoadingSpinner = styled(Box)({
  display: 'inline-block',
  width: '20px',
  height: '20px',
  border: '3px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '50%',
  borderTopColor: 'white',
  animation: `${spin} 1s ease-in-out infinite`,
});